/**
 * Плитка склейки раздела «Товары»: подпись, строки показателей, цена и СПП,
 * подсказки. Прочерк — «данных нет», а не ноль.
 */

import { plural } from "@/lib/plans";
import {
  PROFIT_UNIT,
  ROW_LABEL,
  SPP_ROW,
  TILE_LABEL,
  type NavMetric,
  type TileMetric,
  type TileRowKey,
  type TileValues,
  isCoverKey,
} from "@/lib/productNav";
import type { ModelProduct, SupplierModel } from "@/lib/similar/types";

/** Подпись склейки — ГОЛЫЙ НОМЕР группы (15.09.26 — «просто номер модели
 * склейки», код у поставщика не нужен; 16.09.26 ушло и слово «склейка»:
 * «оно никакой ценности не несёт»). Товар без группы подписан названием. */
export function modelTitle(model: SupplierModel): string {
  return model.group_id !== null ? String(model.group_id) : (model.name ?? "без названия");
}

const UNITS: Record<NavMetric, readonly [string, string, string]> = {
  views: ["показ", "показа", "показов"],
  open_card: ["заход", "захода", "заходов"],
  cart: ["в корзину", "в корзину", "в корзину"],
  orders: ["заказ", "заказа", "заказов"],
  buys: ["выкуп", "выкупа", "выкупов"],
  profit: ["₽", "₽", "₽"],
  reorder: ["шт к заказу", "шт к заказу", "шт к заказу"],
  cover: ["день запаса", "дня запаса", "дней запаса"],
};

/** «1 986 заказов», «−4 210 ₽»; данных за окно нет — «нет данных», а не ноль. */
export function valueLabel(metric: NavMetric, value: number | null): string {
  if (value === null) return isCoverKey(metric) ? "∞ — продаж за окно нет" : "нет данных";
  return `${value.toLocaleString("ru-RU")} ${plural(Math.abs(value), UNITS[metric])}`;
}

/** Число показателя на плитке: подпись стоит слева своей колонкой, поэтому
 *  единицы измерения тут лишние — кроме рубля и процента, которые и есть смысл
 *  числа. Прочерк, а не ноль: «строк за окно нет» и «ноль» — разные ответы. */
export function tileText(metric: TileRowKey, value: number | null | undefined): string {
  if (value === null && isCoverKey(metric)) return "∞";
  if (value === null || value === undefined) return "—";
  if (metric === "profit" || metric === PROFIT_UNIT) return `${value.toLocaleString("ru-RU")} ₽`;
  if (metric === "buyout") {
    return `${value.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
  }
  return value.toLocaleString("ru-RU");
}

/** Строка плитки: подпись, число и признак «это показатель сортировки». */
export type TileRow = { key: string; label: string; text: string; main: boolean };

/** Число или пара «от – до»: границы совпали — одно число, второй границы в
 *  ответе просто нет. Прочерк — «данных нет», а не ноль. */
function диапазон(низ: number | null | undefined, верх: number | null | undefined): string {
  if (низ === null || низ === undefined) return "—";
  const нижнее = низ.toLocaleString("ru-RU");
  return верх === null || верх === undefined ? нижнее : `${нижнее} – ${верх.toLocaleString("ru-RU")}`;
}

/** Строка цены: «13 800 / 8 387» — цена, которая стоит, и сколько платит
 *  клиент. Продаж за неделю не было — одна цена без «/»: ноль кассы соврал бы
 *  «клиент платит ноль», а «платит столько же» — «скидки нет». */
export function priceRow(key: "price_wb" | "price_ozon", values: TileValues | undefined): TileRow {
  const цена = диапазон(values?.[key], values?.[`${key}_max`]);
  const платит = key === "price_wb" ? values?.pays_wb : values?.pays_ozon;
  const платитМакс = key === "price_wb" ? values?.pays_wb_max : values?.pays_ozon_max;
  const касса = платит === null || платит === undefined ? null : диапазон(платит, платитМакс);
  return {
    key,
    label: ROW_LABEL[key],
    text: цена === "—" ? "—" : касса === null ? цена : `${цена} / ${касса}`,
    main: false,
  };
}

/** Строка СПП под своей ценой. Доли НЕТ ВОВСЕ (продаж за неделю не было) —
 *  строки тоже нет: прочерк под ценой читался бы как «СПП ноль». */
export function sppRow(key: "price_wb" | "price_ozon", values: TileValues | undefined): TileRow | null {
  const имя = SPP_ROW[key];
  const доля = values?.[имя];
  if (доля === null || доля === undefined) return null;
  const макс = values?.[`${имя}_max`];
  const число = (n: number) => n.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return {
    key: имя,
    label: ROW_LABEL[имя],
    text: макс === null || макс === undefined ? `${число(доля)} %` : `${число(доля)} – ${число(макс)} %`,
    main: false,
  };
}

/**
 * Строки показателей плитки: ПЕРВЫМ — показатель сортировки (по нему выстроена
 * страница, и плитка без него не отвечала бы на «почему она здесь»), дальше
 * отмеченные в шестерёнке КАНОНИЧЕСКИМ порядком `TILE_LABEL`, без повтора
 * сортировочного. Порядок отметки значения не имеет: плитки обязаны читаться
 * одинаково.
 */
export function tileRows(
  metric: NavMetric,
  show: TileMetric[],
  values: TileValues | undefined,
  value: number | null,
): TileRow[] {
  const rows: TileRow[] = [
    { key: metric, label: ROW_LABEL[metric], text: tileText(metric, value), main: true },
  ];
  // ШТУКА ИДЁТ СРАЗУ ЗА СВОЕЙ СУММОЙ, где бы та ни стояла: у показателя
  // сортировки — под первой строкой, у отмеченной прибыли — под своей.
  const штука = unitRow(values);
  if (metric === "profit" && штука) rows.push(штука);
  for (const key of Object.keys(TILE_LABEL) as TileMetric[]) {
    if (key === metric || !show.includes(key)) continue;
    if (key === "price_wb" || key === "price_ozon") {
      rows.push(priceRow(key, values));
      const доля = sppRow(key, values);
      // СПП идёт СРАЗУ ЗА СВОЕЙ ЦЕНОЙ, как «прибыль/шт» за своей суммой: порознь
      // они читаются как два несвязанных числа.
      if (доля) rows.push(доля);
      continue;
    }
    rows.push({ key, label: ROW_LABEL[key], text: tileText(key, values?.[key]), main: false });
    if (key === "profit" && штука) rows.push(штука);
  }
  return rows;
}

/** Строка «прибыль/шт». Числа НЕТ ВОВСЕ (набор пуст, прибыль не просили) —
 *  строки тоже нет: прочерк под суммой читался бы как «данных нет», хотя их
 *  просто не считали. Прочерк ВНУТРИ строки — другое дело: это «выкупа за окно
 *  не было», и делить не на что. */
function unitRow(values: TileValues | undefined): TileRow | null {
  const got = values?.[PROFIT_UNIT];
  if (got === undefined) return null;
  return {
    key: PROFIT_UNIT,
    label: ROW_LABEL[PROFIT_UNIT],
    text: tileText(PROFIT_UNIT, got),
    main: false,
  };
}

/** Слагаемые выкупа % под подсказку: «выкуп 32,0 % — 96 из 300». Набор без
 *  выкупа либо пустой знаменатель — строки нет вовсе. */
export function buyoutHint(values: TileValues | undefined): string | null {
  if (!values || values.buyout === undefined) return null;
  const выкуплено = values.buys ?? 0;
  const основание = values.deliveries ?? 0;
  if (!основание) return null;
  return `выкуп ${tileText("buyout", values.buyout)} — ${выкуплено.toLocaleString("ru-RU")} из ${основание.toLocaleString("ru-RU")} (доставки WB и заказы Ozon)`;
}

/** Неделя, за которую посчитан СПП, — в подсказку плитки: процент обязан
 *  называть, за какие дни он посчитан. Недели в ответе нет — строки нет. */
export function weekHint(week: { since: string; until: string } | null | undefined): string | null {
  if (!week) return null;
  const день = (iso: string) => iso.slice(8, 10) + "." + iso.slice(5, 7);
  // У каждого товара доля — с его последней недели с продажами, `week` — их
  // общая граница, прошлая полная неделя.
  return `СПП по последней неделе с продажами, не позже ${день(week.until)}`;
}

/** Подсказка товара склейки: цвет, число показателя, название, номер, поставщик. */
export function modelProductHint(p: ModelProduct, metric: NavMetric, supplier: string | null): string {
  return [
    p.color ?? "цвет не задан",
    valueLabel(metric, p.value),
    p.name,
    `товар ${p.product_id}`,
    supplier ? `поставщик ${supplier}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
